import { CircleCheck, CircleDashed, CircleX, ListChecks, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { cn } from '@/lib/utils';
import { providerOf, type DayRange } from '@/lib/automation-replies';
import type { AutomationCheckStatus, AutomationReply } from '@/services/automation-reply.service';

export function ConfidencePill({ value }: { value: number | null }) {
	if (value == null) return null;
	const pct = Math.round(value * 100);
	return (
		<span
			className={cn(
				'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums',
				value >= 0.9 ? 'bg-emerald-50 text-emerald-700' : value >= 0.7 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-900'
			)}
			title='Kim’s confidence in this reply'
		>
			{pct}%
		</span>
	);
}

export function ProviderBadge({ provider }: { provider: string }) {
	const p = providerOf(provider);
	return (
		<span className='inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[12px] text-ink'>
			<span className={cn('h-2 w-2 rounded-full', p.dot)} />
			{p.label}
		</span>
	);
}

/** The Sentinel verdict, or that the reply still waits for one. */
export function ReviewBadge({ review }: { review: AutomationReply['review'] }) {
	if (!review) {
		return <span className='shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-900'>Not reviewed</span>;
	}
	const correct = review.verdict === 'correct';
	const Icon = correct ? CircleCheck : CircleX;
	return (
		<span
			className={cn(
				'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
				correct ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
			)}
			title={review.reviewerName ? `Reviewed by ${review.reviewerName}` : undefined}
		>
			<Icon className='size-3' />
			{correct ? 'Correct' : 'Incorrect'}
		</span>
	);
}

const CHECK_BADGE: Record<AutomationCheckStatus, { label: string; className: string; Icon: typeof CircleCheck }> = {
	pass: { label: 'Checks passed', className: 'bg-emerald-50 text-emerald-700', Icon: ListChecks },
	fail: { label: 'Checks failed', className: 'bg-red-50 text-red-700', Icon: CircleX },
	no_trace: { label: 'No trace', className: 'bg-gray-100 text-gray-900', Icon: CircleDashed }
};

/** How the reply did against its intents' automation checks; nothing until it is checked. */
export function ChecksBadge({ checks }: { checks: AutomationReply['checks'] }) {
	if (!checks) return null;
	const { label, className, Icon } = CHECK_BADGE[checks.status];
	return (
		<span
			className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', className)}
			title={checks.note ?? undefined}
		>
			<Icon className='size-3' />
			{label}
		</span>
	);
}

/** The days a list of sent replies covers, with a way back to every day. `compact` is the icon alone. */
export function SentDateRange({
	value,
	onChange,
	className,
	compact = false
}: {
	value: DayRange;
	onChange: (next: DayRange) => void;
	className?: string;
	compact?: boolean;
}) {
	if (compact) return <DateRangePicker iconOnly value={value} onChange={onChange} ariaLabel='Sent between' className={className} />;
	return (
		<div className={cn('flex items-center gap-1', className)}>
			<DateRangePicker inline value={value} onChange={onChange} ariaLabel='Sent between' className='h-8 flex-1 sm:w-full' />
			{value.startDate && (
				<Button variant='ghost' size='icon-sm' className='rounded-full' aria-label='Any date' onClick={() => onChange({})}>
					<X className='size-3.5' />
				</Button>
			)}
		</div>
	);
}
